import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StarCard from '../components/StarCard';
import { useLanguage } from '../context/LanguageContext';
import { peopleApi, languageToTMDB } from '../services/api';
import { Star, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';

const StarsPage = () => {
  const { t, language } = useLanguage();
  const [stars, setStars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  const fetchStars = useCallback(async (pageNum, query = '') => {
    try {
      setLoading(true);
      const tmdbLang = languageToTMDB[language] || 'en-US';
      
      let data;
      if (query && query.length >= 2) {
        data = await peopleApi.search(query, pageNum, tmdbLang);
      } else {
        data = await peopleApi.getPopular(pageNum, tmdbLang);
      }
      
      setStars(data.results);
      setTotalPages(Math.min(data.total_pages, 50)); // Limit to 50 pages
    } catch (error) {
      console.error('Error fetching stars:', error);
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    fetchStars(page, activeSearch);
  }, [page, activeSearch, fetchStars]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setActiveSearch(searchQuery);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo(0, 0);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  return (
    <div className="bg-[#0B0F0E] min-h-screen">
      <Header />

      <main className="pt-32 pb-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
          {/* Page Header */}
          <div className="flex items-center gap-4 mb-8">
            <Star className="w-10 h-10 text-[#52ea88]" />
            <div>
              <h1 className="text-4xl md:text-5xl font-semibold text-[#E6FFF6]">
                {t('stars.title') || 'Hollywood Stars'}
              </h1>
              <p className="text-[#9FBFB4] mt-2 text-lg">
                {t('stars.description') || 'Explore the actors, directors, and talent that bring cinema to life'}
              </p>
            </div>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mb-8">
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9FBFB4]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('stars.searchPlaceholder') || 'Search for actors, directors...'}
                className="w-full pl-12 pr-24 py-4 bg-[#121917] border border-[#52ea88]/20 rounded-lg text-[#E6FFF6] placeholder-[#9FBFB4]/50 focus:outline-none focus:border-[#52ea88]/50 transition-colors"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-20 top-1/2 -translate-y-1/2 p-1 text-[#9FBFB4] hover:text-[#E6FFF6] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-[#52ea88] text-[#0B0F0E] font-semibold rounded-md hover:bg-[#52ea88]/90 transition-colors"
              >
                {t('stars.search') || 'Search'}
              </button>
            </div>
          </form>

          {/* Active Search Indicator */}
          {activeSearch && (
            <div className="mb-6 flex items-center gap-3">
              <p className="text-[#9FBFB4]">
                {t('stars.searchingFor') || 'Showing results for'}: 
                <span className="text-[#52ea88] font-medium ml-2">"{activeSearch}"</span>
              </p>
              <button
                onClick={clearSearch}
                className="text-sm text-[#52ea88] hover:text-[#E6FFF6] underline transition-colors"
              >
                {t('stars.clearSearch') || 'Clear search'}
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && stars.length === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-64 bg-[#1a2420] rounded-lg animate-pulse" />
              ))}
            </div>
          )}

          {/* Stars Grid */}
          {!loading && stars.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {stars.map((star) => (
                  <StarCard key={star.id} star={star} />
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-center gap-2 mt-12">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="p-2 rounded-lg border border-[#52ea88]/30 text-[#52ea88] hover:bg-[#52ea88]/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                {page > 3 && (
                  <>
                    <button
                      onClick={() => handlePageChange(1)}
                      className="px-4 py-2 rounded-lg border border-[#52ea88]/30 text-[#9FBFB4] hover:text-[#E6FFF6] hover:bg-[#52ea88]/10 transition-colors"
                    >
                      1
                    </button>
                    {page > 4 && <span className="text-[#9FBFB4]">...</span>}
                  </>
                )}

                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-4 py-2 rounded-lg border transition-colors ${
                      pageNum === page
                        ? 'bg-[#52ea88] text-[#0B0F0E] border-[#52ea88] font-semibold'
                        : 'border-[#52ea88]/30 text-[#9FBFB4] hover:text-[#E6FFF6] hover:bg-[#52ea88]/10'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}

                {page < totalPages - 2 && (
                  <>
                    {page < totalPages - 3 && <span className="text-[#9FBFB4]">...</span>}
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      className="px-4 py-2 rounded-lg border border-[#52ea88]/30 text-[#9FBFB4] hover:text-[#E6FFF6] hover:bg-[#52ea88]/10 transition-colors"
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg border border-[#52ea88]/30 text-[#52ea88] hover:bg-[#52ea88]/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Page info */}
              <p className="text-center text-[#9FBFB4] text-sm mt-4">
                {t('stars.page') || 'Page'} {page} {t('stars.of') || 'of'} {totalPages}
              </p>
            </>
          )}

          {/* No Results */}
          {!loading && stars.length === 0 && (
            <div className="text-center py-20">
              <p className="text-[#9FBFB4]/70 text-lg">
                {t('stars.noResults') || 'No stars found'}
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StarsPage;
